import { useEffect, useRef, useState } from "react";
import { FileText, Plus } from "lucide-react";
import { createReportDraft, listReports, setActiveReport } from "../services/db/reportRepo";
import type { Report } from "../services/db/schema";

export function ReportsRoute({ onOpen }: { onOpen: () => Promise<void> }): JSX.Element {
  const [reports, setReports] = useState<Report[]>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>();
  const importInput = useRef<HTMLInputElement>(null);
  const opening = useRef(false);
  useEffect(() => {
    let disposed = false;
    void listReports().then((items) => { if (!disposed) setReports(items); })
      .catch(() => { if (!disposed) setError("Could not open your saved reports. Try reopening the app."); });
    return () => { disposed = true; };
  }, []);

  async function open(reportId?: string): Promise<void> {
    if (opening.current) return;
    opening.current = true;
    setBusy(true);
    setError(undefined);
    try {
      if (reportId) await setActiveReport(reportId);
      else await createReportDraft();
      await onOpen();
    } catch {
      setError("Could not open the report. Your existing reports are still saved on this device.");
    } finally {
      opening.current = false;
      setBusy(false);
    }
  }

  async function backup(reportId: string): Promise<void> {
    if (opening.current) return;
    opening.current = true; setBusy(true); setError(undefined); setMessage(undefined);
    try {
      const { saveDraftArchive } = await import("../services/files/saveDraftArchive");
      const saved = await saveDraftArchive(reportId);
      setMessage(saved ? "Backup prepared. Keep the .mir file in Files, Downloads, or your own cloud drive. It contains private photos and recordings." : "Backup save canceled. Your draft is still on this device.");
    } catch (error) { setError(error instanceof Error ? error.message : "Could not save the backup."); }
    finally { opening.current = false; setBusy(false); }
  }
  async function restore(file: File): Promise<void> {
    if (opening.current) return;
    opening.current = true; setBusy(true); setError(undefined); setMessage(undefined);
    try {
      const { importDraftArchive } = await import("../services/files/draftArchive");
      await importDraftArchive(file);
      await onOpen();
    } catch (error) { setError(error instanceof Error ? error.message : "Could not restore the backup. Existing drafts are safe."); }
    finally { opening.current = false; setBusy(false); if (importInput.current) importInput.current.value = ""; }
  }

  return (
    <section className="screen">
      <div className="topbar">
        <div className="topbar__title">
          <h1>Your reports</h1>
          <p>Save an editable backup to protect your photos, recordings, and notes if this device is lost or the app is removed. Account backups contain finished PDFs.</p>
        </div>
      </div>
      {error && <div className="notice notice--error" role="alert">{error}</div>}
      {message && <p className="notice" role="status">{message}</p>}
      <a className="button" href="/account/reports">Reports saved in your account</a>
      <button className="button button--primary" type="button" disabled={busy || !reports} onClick={() => void open()}>
        <Plus size={18} aria-hidden="true" /> New report
      </button>
      <input ref={importInput} hidden type="file" accept=".mir,application/octet-stream" onChange={(event) => { const file = event.target.files?.[0]; if (file) void restore(file); }} />
      <button className="button" type="button" disabled={busy} onClick={() => importInput.current?.click()}>Restore editable backup</button>
      {!reports && !error && <p className="helper">Opening your reports...</p>}
      {reports?.length === 0 && <p>Your first report starts with a room and a photo.</p>}
      <div className="report-library">
        {reports?.map((report) => (
          <article key={report.id}>
          <button type="button" className="report-library__item"
            disabled={busy} onClick={() => void open(report.id)}>
            <FileText size={22} aria-hidden="true" />
            <span>
              <strong>{report.optionalPropertyAddress || "Move-in report"}</strong>
              <small>{new Date(report.reportCreatedAt).toLocaleString(undefined, {
                year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit"
              })}</small>
              <small>{report.downloadedAt ? "PDF exported" : "Draft"}{report.active ? " - Current report" : ""}</small>
            </span>
          </button>
          <button className="button" type="button" disabled={busy} onClick={() => void backup(report.id)}>Save editable backup</button>
          </article>
        ))}
      </div>
    </section>
  );
}
